import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AddOndutyRequest from './AddOndutyRequest';
import ViewOndutyRequest from './ViewOndutyRequest';

const OndutyRequests = () => {
    const [ondutyRequests, setOndutyRequests] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [, setSalarySettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setErrorMessage] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');
    const [sortBy, setSortBy] = useState('latest');
    const [userRole, setUserRole] = useState('');
    const [userPermissions, setUserPermissions] = useState({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_approve: false,
        loaded: false
    });

    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
    const toBool = (value) => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') return value === 1;
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            return ['1', 'true', 'yes', 'y'].includes(normalized);
        }
        return false;
    };

    const normalizeModuleName = (moduleName) =>
        String(moduleName || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    const getPermissionValue = (permissionModule, keys) => {
        for (const key of keys) {
            if (permissionModule?.[key] !== undefined) return permissionModule[key];
        }
        return undefined;
    };

    const extractPermissionsList = (payload) => {
        if (Array.isArray(payload?.permissions)) return payload.permissions;
        if (Array.isArray(payload?.user_permissions)) return payload.user_permissions;
        if (Array.isArray(payload?.data?.permissions)) return payload.data.permissions;
        if (Array.isArray(payload?.data?.user_permissions)) return payload.data.user_permissions;
        return [];
    };

    // Fetch user role and permissions
    const fetchUserData = useCallback(async () => {
        let currentRole = '';
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_approve: false,
                    loaded: true
                });
                return;
            }

            // Get user role
            const roleResponse = await axios.get(`${API_BASE}/get_users.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (roleResponse.data.success) {
                currentRole = roleResponse.data.role ||
                    roleResponse.data.role_name ||
                    roleResponse.data.user_role || '';
                console.log('User role:', currentRole);
                setUserRole(currentRole);
            }

            const isAdminRole = currentRole === 'Admin' || currentRole === 'Super Admin';
            console.log('Is Admin/Super Admin:', isAdminRole);

            // Get permissions (support both endpoint spellings)
            const permissionApiCandidates = [
                `${API_BASE}/get_user_permssions.php`,
                `${API_BASE}/get_user_permissions.php`
            ];
            let permResponse = null;
            for (const url of permissionApiCandidates) {
                try {
                    const response = await axios.get(url, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    permResponse = response;
                    break;
                } catch (permError) {
                    if (permError.response?.status !== 404) {
                        throw permError;
                    }
                }
            }

            const permissionPayload = permResponse?.data || {};
            const permissionsList = extractPermissionsList(permissionPayload);
            if (permissionsList.length > 0) {
                const ondutyModuleKeys = ['onduty', 'ondutyrequests'];
                const permissionModule = permissionsList.find((p) => {
                    const normalized = normalizeModuleName(p?.module_name);
                    return ondutyModuleKeys.some((key) => normalized.includes(key));
                });

                if (permissionModule) {
                    console.log('Permission module found:', permissionModule);
                    setUserPermissions({
                        can_view: toBool(getPermissionValue(permissionModule, ['can_view', 'view'])),
                        can_add: toBool(getPermissionValue(permissionModule, ['can_add', 'add'])),
                        can_edit: toBool(getPermissionValue(permissionModule, ['can_edit', 'edit'])),
                        can_delete: toBool(getPermissionValue(permissionModule, ['can_delete', 'delete'])),
                        can_approve: toBool(getPermissionValue(permissionModule, ['can_approve', 'approve', 'set_user_permission', 'can_process', 'process'])),
                        loaded: true
                    });
                } else {
                    // Default permissions if module not found
                    setUserPermissions({
                        can_view: true,
                        can_add: true,
                        can_edit: isAdminRole,
                        can_delete: isAdminRole,
                        can_approve: isAdminRole,
                        loaded: true
                    });
                }
            } else {
                // Default permissions if no permission data
                setUserPermissions({
                    can_view: true,
                    can_add: true,
                    can_edit: isAdminRole,
                    can_delete: isAdminRole,
                    can_approve: isAdminRole,
                    loaded: true
                });
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            const isAdminRole = currentRole === 'Admin' || currentRole === 'Super Admin';
            setUserPermissions({
                can_view: true,
                can_add: true,
                can_edit: isAdminRole,
                can_delete: isAdminRole,
                can_approve: isAdminRole,
                loaded: true
            });
        }
    }, [API_BASE]);

    // Fetch onduty requests
    const fetchOndutyRequests = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setErrorMessage('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}onduty/get_onduty_requests.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setOndutyRequests(response.data.onduty_requests || []);
                setErrorMessage('');
            } else {
                setOndutyRequests([]);
                const errorMsg = response.data?.error || 'Failed to fetch onduty requests';
                setErrorMessage(errorMsg);
            }
        } catch (error) {
            setOndutyRequests([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch onduty requests';
            setErrorMessage(errorMsg);
            console.error('Error fetching onduty requests:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    // Fetch employees
    const fetchEmployees = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.get(`${API_BASE}/get_employees.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setEmployees(response.data.employees || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    }, [API_BASE]);

    // Fetch salary settings
    const fetchSalarySettings = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.get(`${API_BASE}salarysettings/get_salary_settings.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setSalarySettings(response.data.salary_settings || []);
            }
        } catch (error) {
            console.error('Error fetching salary settings:', error);
        }
    }, [API_BASE]);

    useEffect(() => {
        const initializeData = async () => {
            try {
                await fetchUserData();
                await Promise.all([
                    fetchOndutyRequests(),
                    fetchEmployees(),
                    fetchSalarySettings()
                ]);
            } catch (error) {
                console.error('Initialization error:', error);
                setErrorMessage(`Initialization failed: ${error.message}`);
            }
        };

        initializeData();
    }, [fetchOndutyRequests, fetchEmployees, fetchSalarySettings, fetchUserData]);

    // Permission check functions
    const canView = () => userPermissions.can_view;

    const canAdd = () => userPermissions.can_add;

    const canApprove = () => {
        return userPermissions.can_approve;
    };

    const isPendingStatus = (status) => String(status || '').trim().toLowerCase() === 'pending';

    const handleAdd = () => {
        if (!canAdd()) {
            alert('You do not have permission to add onduty requests');
            return;
        }
        setShowAddModal(true);
    };

    const handleView = (request) => {
        setSelectedRequest(request);
        setShowViewModal(true);
    };

    const getEmployeeName = (empId) => {
        const employee = employees.find(emp => emp.emp_id === empId);
        return employee ? `${employee.first_name} ${employee.last_name}` : `Employee ${empId}`;
    };

    const getEmployeeCode = (empId) => {
        const employee = employees.find(emp => emp.emp_id === empId);
        return employee ? employee.emp_code : '';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const isDateBasedRequest = (request) => {
        const timingMode = String(request?.timing_mode || '').toLowerCase();
        const requestType = String(request?.request_type || '').toLowerCase();
        return timingMode.includes('date') || requestType.includes('date');
    };

    const getDateRangeText = (request) => {
        if (isDateBasedRequest(request)) {
            return `${formatDate(request.from_date)} - ${formatDate(request.to_date)}`;
        }
        return `${formatDate(request.from_datetime)} - ${formatDate(request.to_datetime)}`;
    };

    const handleApprove = async (ondutyId, action) => {
        const confirmMessage = action === 'approve'
            ? 'Are you sure you want to approve this onduty request?'
            : 'Are you sure you want to reject this onduty request?';

        if (!window.confirm(confirmMessage)) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');

            const response = await axios.post(`${API_BASE}onduty/onduty_approve.php`,
                {
                    onduty_id: ondutyId,
                    status: action === 'approve' ? 'Approved' : 'Rejected'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                await fetchOndutyRequests();
                alert(`Onduty request ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
            } else {
                setErrorMessage(response.data.error || `Failed to ${action} onduty request`);
                alert(response.data.error || `Failed to ${action} onduty request`);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || `Failed to ${action} onduty request`;
            setErrorMessage(errorMsg);
            alert(errorMsg);
            console.error(`Error ${action}ing onduty request:`, error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (ondutyId) => {
        if (!window.confirm('Are you sure you want to cancel this onduty request?')) return;

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}onduty/update_onduty_status.php`,
                {
                    onduty_id: ondutyId,
                    status: 'Cancelled'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                await fetchOndutyRequests();
                alert('Onduty request cancelled successfully');
            } else {
                setErrorMessage(response.data.error || 'Failed to cancel onduty request');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Failed to cancel onduty request';
            setErrorMessage(errorMsg);
            console.error('Error cancelling onduty request:', error);
        }
    };

    const filteredRequests = ondutyRequests.filter(request => {
        const employee = employees.find(emp => emp.emp_id === request.emp_id);
        const matchesSearch = employee ?
            employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.emp_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.purpose?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.destination?.toLowerCase().includes(searchTerm.toLowerCase()) :
            request.emp_id?.toString().includes(searchTerm.toLowerCase()) ||
            request.purpose?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = filterStatus === 'All' || request.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    const sortedRequests = [...filteredRequests].sort((a, b) => {
        switch (sortBy) {
            case 'latest':
                return new Date(b.created_at) - new Date(a.created_at);
            case 'oldest':
                return new Date(a.created_at) - new Date(b.created_at);
            case 'date':
                return new Date(b.from_date) - new Date(a.from_date);
            case 'employee':
                return getEmployeeName(a.emp_id).localeCompare(getEmployeeName(b.emp_id));
            case 'status':
                return a.status.localeCompare(b.status);
            default:
                return b.onduty_id - a.onduty_id;
        }
    });

    console.log('Current state:', {
        userRole,
        userPermissions,
        canApprove: canApprove(),
        pendingRequests: ondutyRequests.filter(r => r.status === 'Pending').length
    });

    if ((loading && ondutyRequests.length === 0) || !userPermissions.loaded) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading onduty requests...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!canView()) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                                <h3 className="mt-3 text-warning">Access Denied</h3>
                                <p className="text-muted">You do not have permission to view onduty requests.</p>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    <i className="bi bi-speedometer2 me-2"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3" role="group">
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/dashboard')}
                                                title="Go to Dashboard"
                                            >
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/Menu')}
                                                title="Go to Menu"
                                            >
                                                <i className="bi bi-menu-button"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-briefcase me-2 text-primary"></i>
                                                On-Duty Requests
                                            </h2>
                                            <p className="text-muted mb-0">
                                                Manage employee on-duty requests for official purposes
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        {canAdd() && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleAdd}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                New On-Duty Request
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row g-3 align-items-center">
                                <div className="col-md-4">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search by employee name, code, purpose, or destination..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                    >
                                        <option value="All">All Status</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Rejected">Rejected</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="latest">Newest First</option>
                                        <option value="oldest">Oldest First</option>
                                        <option value="date">Sort by Date</option>
                                        <option value="employee">Sort by Employee</option>
                                        <option value="status">Sort by Status</option>
                                    </select>
                                </div>
                                <div className="col-md-2 text-end">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                            fetchOndutyRequests();
                                        }}
                                        title="Refresh"
                                    >
                                        <i className="bi bi-arrow-clockwise"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* On-Duty Requests Table */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body p-0">
                            {sortedRequests.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-briefcase display-1 text-muted"></i>
                                    <h4 className="mt-3 text-muted">No on-duty requests found</h4>
                                    <p className="text-muted">
                                        {ondutyRequests.length === 0
                                            ? "No on-duty requests available. Create your first request."
                                            : "No requests match your search criteria."
                                        }
                                    </p>
                                    {ondutyRequests.length === 0 && canAdd() && (
                                        <button
                                            className="btn btn-primary mt-2"
                                            onClick={handleAdd}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Create On-Duty Request
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4">Employee</th>
                                                <th>Date Range</th>
                                                <th>Duration</th>
                                                <th>Purpose</th>
                                                <th>Destination</th>
                                                <th>Status</th>
                                                <th>Submitted</th>
                                                <th className="text-end pe-4">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedRequests.map((request) => {
                                                const showApproveButtons = isPendingStatus(request.status);

                                                return (
                                                    <tr key={request.onduty_id}>
                                                        <td className="ps-4">
                                                            <div className="d-flex align-items-center">
                                                                <div className="employee-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                                                    style={{ width: '40px', height: '40px' }}
                                                                >
                                                                    <i className="bi bi-person"></i>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-semibold">
                                                                        {getEmployeeName(request.emp_id)}
                                                                    </div>
                                                                    <small className="text-muted d-block">
                                                                        ID: {request.emp_id} | Code: {request.emp_code || getEmployeeCode(request.emp_id)}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <div className="fw-semibold">
                                                                    {getDateRangeText(request)}
                                                                </div>
                                                                {Number(request.first_day_half) === 1 && (
                                                                    <small className="text-muted d-block">
                                                                        First day: {request.first_day_period} (Half)
                                                                    </small>
                                                                )}
                                                                {Number(request.last_day_half) === 1 && (
                                                                    <small className="text-muted d-block">
                                                                        Last day: {request.last_day_period} (Half)
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-primary">
                                                                {request.total_days} days
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={request.purpose}>
                                                                {request.purpose || 'No purpose provided'}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {request.destination || '-'}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${request.status === 'Pending' ? 'bg-warning' :
                                                                request.status === 'Approved' ? 'bg-success' :
                                                                    request.status === 'Rejected' ? 'bg-danger' :
                                                                        'bg-secondary'
                                                                }`}>
                                                                {request.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <small className="text-muted d-block">
                                                                {formatDate(request.created_at)}
                                                            </small>
                                                            <small className="text-muted">
                                                                {new Date(request.created_at).toLocaleTimeString()}
                                                            </small>
                                                        </td>
                                                        <td className="text-end pe-4">
                                                            <div className="btn-group" role="group">
                                                                <button
                                                                    className="btn btn-outline-info btn-sm"
                                                                    onClick={() => handleView(request)}
                                                                    title="View Details"
                                                                >
                                                                    <i className="bi bi-eye"></i>
                                                                </button>

                                                                {showApproveButtons && (
                                                                    <>
                                                                        <button
                                                                            className="btn btn-outline-success btn-sm"
                                                                            onClick={() => handleApprove(request.onduty_id, 'approve')}
                                                                            title="Approve"
                                                                            aria-label="Approve"
                                                                        >
                                                                            <i className="bi bi-check-lg"></i>
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-outline-danger btn-sm"
                                                                            onClick={() => handleApprove(request.onduty_id, 'reject')}
                                                                            title="Reject"
                                                                            aria-label="Reject"
                                                                        >
                                                                            <i className="bi bi-x-lg"></i>
                                                                        </button>
                                                                    </>
                                                                )}

                                                                {isPendingStatus(request.status) && (
                                                                    <button
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => handleCancel(request.onduty_id)}
                                                                        title="Cancel"
                                                                    >
                                                                        <i className="bi bi-slash-circle"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {showAddModal && canAdd() && (
                <AddOndutyRequest
                    show={showAddModal}
                    onHide={() => setShowAddModal(false)}
                    employees={employees}
                    onOndutyAdded={fetchOndutyRequests}
                />
            )}

            {showViewModal && selectedRequest && (
                <ViewOndutyRequest
                    show={showViewModal}
                    onHide={() => setShowViewModal(false)}
                    request={selectedRequest}
                    employeeName={getEmployeeName(selectedRequest.emp_id)}
                    employeeCode={selectedRequest.emp_code || getEmployeeCode(selectedRequest.emp_id)}
                    canApprove={canApprove()}
                    userRole={userRole}
                    onStatusChange={fetchOndutyRequests}
                />
            )}
        </div>
    );
};

export default OndutyRequests;
